import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";

/**
 * Emergent OAuth landing route.
 * Google redirects the user to `/auth/callback#session_id=xxx`.
 * We read the fragment ONCE, exchange it for a Samara JWT via
 * `POST /api/auth/google/callback`, then route to the correct dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const { googleLogin } = useAuth();
  const nav = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    // useRef prevents StrictMode double-invoke from spending the session_id twice.
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) {
      toast.error("Google session missing. Please try again.");
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const user = await googleLogin(sessionId);
        // Strip the session_id fragment so it can't be reused
        window.history.replaceState({}, "", "/auth/callback");
        toast.success(`Welcome, ${user.name || user.email}`);
        const target =
          user.role === "admin" ? "/admin"
          : user.role === "franchise" ? "/franchise"
          : "/dashboard";
        nav(target, { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Google login failed");
        nav("/login", { replace: true });
      }
    })();
  }, [googleLogin, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-sand">
      <div className="text-center space-y-4">
        <Logo />
        <div className="text-xs uppercase tracking-[0.4em] text-gold">Signing you in</div>
        <div className="h-1 w-40 mx-auto bg-navy/10 overflow-hidden rounded-full">
          <div className="h-full w-1/2 bg-gold animate-pulse" />
        </div>
        <p className="text-sm text-navy/60">Finishing your Google sign-in…</p>
      </div>
    </div>
  );
}
